/**
 * Authentication Context
 * Provides authentication state and methods throughout the application
 *
 * Requirements: 2.1, 1.1, 5.1, 4.2, 11.5
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/apiService';
import { getToken, removeToken } from '../services/tokenManager';
import { authEvents } from '../services/authEvents';

/**
 * @typedef {Object} AuthContextType
 * @property {Object|null} user
 * @property {string|null} token
 * @property {boolean} isAuthenticated
 * @property {boolean} isLoading
 * @property {Function} login
 * @property {Function} register
 * @property {Function} logout
 * @property {Function} updateProfile
 */

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!token;

  // Initialize authentication state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = getToken();

      if (storedToken) {
        try {
          const userData = await apiService.getProfile();
          setUser(userData);
          setTokenState(storedToken);
        } catch (error) {
          // Requirement: 11.3 - Token is invalid or expired, clear it
          console.error('Token validation failed:', error);
          removeToken();
          setUser(null);
          setTokenState(null);
        }
      }

      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // Subscribe to 401 events from the API interceptor
  // Clears local auth state and redirects to login
  useEffect(() => {
    const unsubscribe = authEvents.onUnauthorized(() => {
      setUser(null);
      setTokenState(null);
      // Hard redirect: works regardless of where in the React tree the error occurs
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    });

    return unsubscribe;
  }, []);

  const register = async (data) => {
    try {
      const response = await apiService.register(data);
      setUser(response.user);
      setTokenState(response.access_token);
      return response;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const login = async (email, password, twoFactorCode = null) => {
    try {
      const loginData = { email, password };
      if (twoFactorCode) {
        loginData.two_factor_code = twoFactorCode;
      }

      const response = await apiService.login(loginData);

      if (response.requires_2fa) {
        return response;
      }

      setUser(response.user);
      setTokenState(response.access_token);
      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      setUser(null);
      setTokenState(null);
    }
  };

  const updateProfile = async (data) => {
    try {
      const updatedUser = await apiService.updateProfile(data);
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('Profile update failed:', error);
      throw error;
    }
  };

  const value = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
